import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, finalize, of } from "rxjs";
import {
  BgmTrack,
  MultiVideoPlayerComponent,
  VoTrack,
} from "../multi-video-player/multi-video-player.component";
import { ExternalShareService } from "src/app/services/external-share.service";
import {
  ShareHeaderBreadcrumb,
  ShareHeaderComponent,
} from "src/app/external/share-header/share-header.component";
import { LOCAL_STORAGE_KEYS, assetName } from "src/app/global/constant";

@Component({
  selector: "app-multi-video-player-demo",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiVideoPlayerComponent,
    ShareHeaderComponent,
  ],
  templateUrl: "./multi-video-player-demo.component.html",
  styleUrl: "./multi-video-player-demo.component.scss",
})
export class MultiVideoPlayerDemoComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  shareIdInput: string = "";
  activeUrls: string[] = [];

  loading = false;
  error: string | null = null;
  totalShots = 0;
  approvedShots = 0;

  voInputText: string = `[]`;
  voTracks: VoTrack[] = [];
  voError: string | null = null;

  bgmInputText: string = "";
  bgmTrack: BgmTrack | null = null;
  bgmError: string | null = null;

  assetName = assetName;

  // Header state — mirrors VideoShareComponent
  headerBreadcrumb: ShareHeaderBreadcrumb = {
    project: "",
    title: "Video",
    stageBadge: "Video",
    stageBadgeClass: "badge-video",
  };
  user: any = null;
  assetId: string = "";
  videoId: string | number = "";

  constructor(
    private route: ActivatedRoute,
    private externalShareService: ExternalShareService,
  ) {}

  ngOnInit(): void {
    const routeShareId = this.route.snapshot.paramMap.get("shareId");
    const queryShareId = this.route.snapshot.queryParamMap.get("share_id");
    const shareId = routeShareId || queryShareId;
    if (shareId) {
      this.shareIdInput = shareId;
      this.heartbeat();
      this.loadFromShare();
    }
    this.applyVoTracks();
    this.applyBgm();
  }

  private heartbeat(): void {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.accessToken) || "";
    const shareId = (this.shareIdInput || "").trim();
    if (!token || !shareId) return;

    this.externalShareService
      .heartbeat({
        token,
        share_id: shareId,
        asset_name: assetName.video,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: any) => {
        if (!res) return;
        const data = res?.data ?? res;
        if (data?.asset_id) {
          this.assetId = String(data.asset_id);
        }
        if (data?.users) {
          this.user = {
            user_id: String(data.users.id),
            firstname: data.users.firstname,
            lastname: data.users.lastname,
            profile: data.users.profile,
            email: data.users.email,
            role: data.role,
          };
        }
        if (data?.campaign_name || data?.video_name) {
          this.headerBreadcrumb = {
            project: data.campaign_name ?? this.headerBreadcrumb.project,
            title: data.video_name ?? this.headerBreadcrumb.title,
            stageBadge: "Video",
            stageBadgeClass: "badge-video",
          };
        }
        if (data?.video_id) {
          this.videoId = data.video_id;
        }
      });
  }

  loadFromShare(): void {
    const shareId = (this.shareIdInput || "").trim();
    if (!shareId) {
      this.error = "Enter a share ID first.";
      return;
    }
    this.error = null;
    this.loading = true;
    this.activeUrls = [];
    this.approvedShots = 0;
    this.totalShots = 0;

    this.externalShareService
      .getVideo(shareId)
      .pipe(
        catchError(() => of(null)),
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: any) => {
        if (!res) {
          this.error =
            "Failed to load video. Check the share ID and that you're logged in.";
          return;
        }
        const data = res?.data ?? res;
        const videox = data?.videox;
        if (videox) {
          if (videox.campaign_name || videox.video_name) {
            this.headerBreadcrumb = {
              project: videox.campaign_name ?? this.headerBreadcrumb.project,
              title: videox.video_name ?? this.headerBreadcrumb.title,
              stageBadge: "Video",
              stageBadgeClass: "badge-video",
            };
          }
          if (!this.assetId && videox.id) {
            this.assetId = String(videox.id);
          }
          if (!this.videoId && videox.id) {
            this.videoId = videox.id;
          }
        }

        const rawShots = data?.shotlist_scene ?? [];
        this.totalShots = Array.isArray(rawShots) ? rawShots.length : 0;

        const approvedUrls: string[] = [];
        for (const shot of rawShots) {
          const url = shot?.approved_video?.url;
          if (typeof url === "string" && url.length > 0) {
            approvedUrls.push(url);
          }
        }
        this.approvedShots = approvedUrls.length;
        this.activeUrls = approvedUrls;

        if (!approvedUrls.length) {
          this.error = `No approved videos in this share (${this.totalShots} shot${this.totalShots === 1 ? "" : "s"} total).`;
        }
      });
  }

  applyVoTracks(): void {
    const text = (this.voInputText || "").trim();
    if (!text) {
      this.voTracks = [];
      this.voError = null;
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        this.voError = "Expected a JSON array of VO tracks.";
        return;
      }
      const valid: VoTrack[] = [];
      for (const item of parsed) {
        if (
          item &&
          typeof item.url === "string" &&
          typeof item.startTime === "number"
        ) {
          valid.push({
            id: item.id,
            url: item.url,
            startTime: item.startTime,
            duration: typeof item.duration === "number" ? item.duration : undefined,
            volume: typeof item.volume === "number" ? item.volume : undefined,
            duckVideoTo:
              typeof item.duckVideoTo === "number"
                ? item.duckVideoTo
                : undefined,
          });
        }
      }
      this.voTracks = valid;
      this.voError = valid.length
        ? null
        : "No valid VO tracks found. Each item needs at least { url, startTime }.";
    } catch (e: any) {
      this.voError = "Invalid JSON: " + (e?.message ?? "parse error");
    }
  }

  clearVoTracks(): void {
    this.voInputText = "";
    this.voTracks = [];
    this.voError = null;
  }

  applyBgm(): void {
    const text = (this.bgmInputText || "").trim();
    if (!text) {
      this.bgmTrack = null;
      this.bgmError = null;
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed.url !== "string" || !parsed.url) {
        this.bgmError = "Expected a JSON object with a 'url' string.";
        return;
      }
      this.bgmTrack = {
        url: parsed.url,
        volume: typeof parsed.volume === "number" ? parsed.volume : undefined,
        loop: typeof parsed.loop === "boolean" ? parsed.loop : undefined,
        duckedByVoTo:
          typeof parsed.duckedByVoTo === "number"
            ? parsed.duckedByVoTo
            : undefined,
      };
      this.bgmError = null;
    } catch (e: any) {
      this.bgmError = "Invalid JSON: " + (e?.message ?? "parse error");
    }
  }

  clearBgm(): void {
    this.bgmInputText = "";
    this.bgmTrack = null;
    this.bgmError = null;
  }
}
